// ─── Manage Camera → Detectors ───────────────────────────────────────────────
//
// Add or remove the detection modules a camera runs. A camera runs exactly the
// detectors in its `detectors` list — new cameras get ["unknown"] — and until this
// tab only the industrial AI Modules page and Guided Setup could change it, so on a
// non-industrial site nobody could add a module to a camera after setup.
//
// One catalogue (utils/aiModels.js), the same rules as the AI Modules page:
//   * face identity (unknown / known / banned) is ONE switch, exactly as the AI
//     Modules page drives it with FACE_ON_SET — this tab cannot produce a face set
//     the rest of the product does not expect;
//   * module settings save only values that differ from the detector's default
//     (nonDefault), so opening this tab never pins anything.
// Saves {detectors, detector_config} through PUT /cameras/{idx}, the body the AI
// Modules page sends; the box reloads that camera's worker when either changes.

import { useEffect, useState } from 'react';
import { apiFetch } from '../utils/streamApi';
import { MODELS, FACE_AWARE, FACE_ON_SET, MODEL_SETTINGS, nonDefault } from '../utils/aiModels';
import { SettingRow } from './setup/SetupBits';
import FaceCoverPanel from './FaceCoverPanel';

const row = { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0',
              borderBottom: '1px solid var(--border)' };

export default function CameraDetectorsTab({ cam, idx, onSaved }) {
  const [live, setLive]       = useState(null);     // the camera as the box has it now
  const [enabled, setEnabled] = useState([]);
  const [tuning, setTuning]   = useState({});
  const [dirty, setDirty]     = useState(false);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState(null);

  // Fresh from the box: the list row this modal was opened from may predate a save
  // made on the AI Modules page.
  useEffect(() => {
    let ok = true;
    apiFetch('/ui/cameras').then(r => (r.ok ? r.json() : null)).then(d => {
      if (!ok) return;
      const c = ((d && d.cameras) || []).find(x => x.idx === idx) || cam;
      setLive(c);
      setEnabled(Array.isArray(c.detectors) ? [...c.detectors] : []);
      const t = {};
      Object.keys(MODEL_SETTINGS).forEach(k => {
        const s = (c.detector_config || {})[k] || {};
        t[k] = Object.fromEntries(MODEL_SETTINGS[k].map(x => [x.key, s[x.key] != null ? s[x.key] : x.def]));
      });
      setTuning(t);
    }).catch(() => { if (ok) setLive(cam); });
    return () => { ok = false; };
  }, [idx, cam]);

  if (!live) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>;

  const aiOff  = (live.detect_mode || 'full') !== 'full';
  const faceOn = enabled.some(k => FACE_AWARE.includes(k));
  const touch  = fn => { setEnabled(fn); setDirty(true); setMsg(null); };
  const toggle = key => touch(e => (e.includes(key) ? e.filter(k => k !== key) : [...e, key]));
  const setFace = on => touch(e => (on
    ? [...new Set([...e.filter(k => k !== 'instant'), ...FACE_ON_SET])]
    : [...new Set([...e.filter(k => !FACE_AWARE.includes(k)), 'instant'])]));
  const setTune = (model, key, val) => {
    setTuning(t => ({ ...t, [model]: { ...(t[model] || {}), [key]: val } }));
    setDirty(true); setMsg(null);
  };

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const body = { detectors: enabled };
      const dc = {};
      enabled.forEach(k => {
        if (!MODEL_SETTINGS[k] || !tuning[k]) return;
        const diff = nonDefault(k, tuning[k], (live.detector_config || {})[k]);
        if (Object.keys(diff).length) dc[k] = diff;
      });
      if (Object.keys(dc).length) body.detector_config = dc;
      const r = await apiFetch(`/cameras/${idx}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      setLive(d.camera || { ...live, detectors: [...enabled] });
      setDirty(false);
      setMsg({ ok: true, text: 'Saved — the camera reloads to apply the new detectors.' });
      onSaved && d.camera && onSaved(d.camera);
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Save failed' });
    } finally { setBusy(false); }
  };

  const fcLive = (live.detectors || []).includes('face_cover');

  return (
    <div>
      <label style={{ fontWeight: 600, fontSize: 13 }}>Detectors on this camera</label>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 8px' }}>
        Which AI modules run on this camera. Saving reloads the camera so the change applies right away.
      </p>
      {aiOff && (
        <div className="adm-msg error" style={{ marginBottom: 8 }}>
          AI is {live.detect_mode === 'off' ? 'off' : 'check-ins only'} on this camera (Basic tab) — detectors will not run.
        </div>
      )}

      <div style={{ opacity: aiOff ? 0.5 : 1, pointerEvents: aiOff ? 'none' : 'auto' }}>
        <div style={row}>
          <input type="checkbox" checked={faceOn} onChange={e => setFace(e.target.checked)} style={{ marginTop: 3 }} />
          <div>
            <b>🧑 Face recognition</b>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              On: alerts on people it does not recognise, quiet for enrolled staff, watches the banned list.
              Off: alerts on any person without working out who.
            </div>
          </div>
        </div>
        {MODELS.map(m => (
          <div key={m.key}>
            <div style={row}>
              <input type="checkbox" checked={enabled.includes(m.key)} onChange={() => toggle(m.key)}
                     style={{ marginTop: 3 }} />
              <div style={{ flex: 1 }}>
                <b>{m.icon} {m.label}</b>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{m.desc}</div>
              </div>
            </div>
            {m.key === 'face_cover' && enabled.includes('face_cover') && (
              <div style={{ padding: '8px 0 8px 26px' }}>
                {MODEL_SETTINGS.face_cover.map(s => (
                  <SettingRow key={s.key} spec={s} value={tuning.face_cover?.[s.key]}
                    overridden={(live.detector_config?.face_cover || {})[s.key] != null}
                    onChange={v => setTune('face_cover', s.key, v)} />
                ))}
                {fcLive
                  ? <FaceCoverPanel cameraIdx={idx} camera={live} />
                  : <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      Save to turn it on — its area, hours and false-alert monitoring then appear here.
                    </p>}
              </div>
            )}
          </div>
        ))}
      </div>

      {!enabled.length && (
        <div className="adm-msg error" style={{ marginTop: 8 }}>
          No detectors — this camera will raise no alerts. Use “AI off” on the Basic tab if that is what you want.
        </div>
      )}
      <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button type="button" className="adm-btn primary" disabled={busy || !dirty} onClick={save}>
          {busy ? 'Saving…' : 'Save detectors'}
        </button>
        {msg && <span style={{ fontSize: 12, color: msg.ok ? '#22c55e' : '#ef4444' }}>{msg.text}</span>}
        {!msg && dirty && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Unsaved changes on this tab.</span>}
      </div>
    </div>
  );
}
